/**
 * EnumHeaderGlyph — clickable enum-state glyph rendered in a result-grid
 * column header. Three states: declared (blue ▾), native (gray ▾), eligible
 * (gray ▾?). Presentational only — parent passes a resolved state + an
 * onClick wrapper. Shared by TableExplorerGrid and the Query Editor
 * (useCellRenderer). Uses NEW grid-header-enum-glyph-* testids, distinct
 * from GridHeaderRow's context-menu grid-header-enum-* items.
 */

import React from 'react';

import { GLYPH_DECLARED_CLASS, GLYPH_MUTED_CLASS } from './gridGlyphStyles';

export type EnumGlyphState = 'declared' | 'native' | 'eligible';

type EnumHeaderGlyphProps = {
  state: EnumGlyphState;
  onClick: () => void;
};

const CONFIG: Record<
  EnumGlyphState,
  { testId: string; label: string; title: string; className: string }
> = {
  declared: {
    testId: 'grid-header-enum-glyph-declared',
    label: '▾',
    title: 'Declared as enum — click for details',
    className: GLYPH_DECLARED_CLASS,
  },
  native: {
    testId: 'grid-header-enum-glyph-native',
    label: '▾',
    title: 'Native enum column — click to configure',
    className: GLYPH_MUTED_CLASS,
  },
  eligible: {
    testId: 'grid-header-enum-glyph-eligible',
    label: '▾?',
    title: 'Looks like an enum — click to declare',
    className: GLYPH_MUTED_CLASS,
  },
};

export const EnumHeaderGlyph = React.memo(function EnumHeaderGlyph({
  state,
  onClick,
}: EnumHeaderGlyphProps) {
  const c = CONFIG[state];
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={c.className}
      title={c.title}
      aria-label={c.title}
      data-testid={c.testId}
    >
      {c.label}
    </button>
  );
});
